import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { ContactPopup } from '../components/ContactPopup';
import {
  heroData,
  companyInfo,
  aboutData,
  trademarkOverview,
  servicesData,
  whyChooseUs,
  processSteps,
  pricingPlans,
  industriesServed,
  benefits,
  trustSignals,
  faqData,
  ctaData,
} from '../mock';
import {
  Search,
  FileText,
  Shield,
  Eye,
  Lock,
  CheckCircle,
  Laptop,
  ShoppingBag,
  Coffee,
  Heart,
  Briefcase,
  Factory,
  Film,
  Shirt,
  Home,
  GraduationCap,
  Car,
  DollarSign,
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  Award,
  Users,
  TrendingUp,
  Star,
  ArrowRight,
  Check,
  Trophy,
  Zap,
} from 'lucide-react';

const iconMap = {
  Search, FileText, Shield, Eye, Lock, CheckCircle,
  Laptop, ShoppingBag, Coffee, Heart, Briefcase, Factory,
  Film, Shirt, Home, GraduationCap, Car, DollarSign, Trophy,
};

export default function HomePage() {
  const [popupOpen, setPopupOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'services', label: 'Services' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'faq', label: 'FAQ' },
    { id: 'contact', label: 'Contact' },
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMobileMenuOpen(false);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = navItems.map(item => item.id);
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Professional Trademark Registration Services | USPTO Filing Experts</title>
        <meta name="title" content="Professional Trademark Registration Services | USPTO Filing Experts" />
        <meta name="description" content="Expert trademark registration services in the United States. Fast, reliable USPTO filing with 98% success rate. Protect your brand with comprehensive trademark search, filing, and monitoring services. Starting at $45." />
        <meta name="keywords" content="trademark registration, USPTO filing, trademark search, brand protection, trademark attorney, intellectual property, trademark services, trademark application, register trademark, trademark filing online" />
        <link rel="canonical" href={currentUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="Professional Trademark Registration Services | USPTO Filing Experts" />
        <meta property="og:description" content="Expert trademark registration services with 98% success rate. Fast, reliable USPTO filing starting at $45. Protect your brand today." />
        <meta property="og:image" content={heroData.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content="Professional Trademark Registration Services | USPTO Filing Experts" />
        <meta name="twitter:description" content="Expert trademark registration services with 98% success rate. Fast, reliable USPTO filing starting at $45. Protect your brand today." />
        <meta name="twitter:image" content={heroData.image} />

        {/* Additional SEO Meta Tags */}
        <meta name="geo.region" content="US-CA" />
        <meta name="geo.placename" content="San Diego" />
        <meta name="geo.position" content="32.715738;-117.161084" />
        <meta name="ICBM" content="32.715738, -117.161084" />

        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            "name": "Trademark Service Company",
            "image": heroData.image,
            "description": "Professional trademark registration services in the United States",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "1420 Kettner Blvd suite 100",
              "addressLocality": "San Diego",
              "addressRegion": "CA",
              "postalCode": "92101",
              "addressCountry": "US"
            },
            "telephone": "+1-123-123-4567",
            "email": "info@trademarkservicecompany.com",
            "url": currentUrl,
            "priceRange": "$45-$349",
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500"
            }
          })}
        </script>

        {/* Structured Data - Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Trademark Registration",
            "provider": {
              "@type": "ProfessionalService",
              "name": "Trademark Service Company"
            },
            "areaServed": {
              "@type": "Country",
              "name": "United States"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Trademark Registration Services",
              "itemListElement": pricingPlans.map((plan, index) => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": `${plan.name} Package`
                },
                "price": plan.price.replace('$', ''),
                "priceCurrency": "USD"
              }))
            }
          })}
        </script>

        {/* Structured Data - FAQPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqData.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-md shadow-md z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex-shrink-0">
              <h1 className="text-xl font-bold text-[#1e3a8a]">
                {companyInfo.name}
              </h1>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`text-sm font-medium hover:text-[#f59e0b] transition-colors ${
                    activeSection === item.id ? 'text-[#f59e0b]' : 'text-gray-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            <div className="hidden md:block">
              <Button
                onClick={() => setPopupOpen(true)}
                className="bg-gradient-to-r from-[#f59e0b] to-[#d97706] hover:from-[#d97706] hover:to-[#f59e0b] text-white"
              >
                Get Started
              </Button>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-700 hover:text-[#1e3a8a]"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left px-3 py-2 rounded-md text-base font-medium ${
                    activeSection === item.id
                      ? 'bg-[#1e3a8a] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </button>
              ))}
              <Button
                onClick={() => {
                  setPopupOpen(true);
                  setMobileMenuOpen(false);
                }}
                className="w-full bg-gradient-to-r from-[#f59e0b] to-[#d97706] text-white mt-2"
              >
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section
        id="home"
        className="pt-24 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1e3a8a]/5 to-[#f59e0b]/5"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-[#f59e0b] text-white hover:bg-[#d97706]">
                USPTO Filing Experts
              </Badge>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#1e3a8a] leading-tight">
                {heroData.title}
              </h1>
              <p className="text-lg text-gray-700">
                {heroData.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={() => setPopupOpen(true)}
                  size="lg"
                  className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#1e3a8a] text-white font-semibold px-8 py-6 text-lg group"
                >
                  Register My Trademark
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button
                  onClick={() => scrollToSection('pricing')}
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#1e3a8a] text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white px-8 py-6 text-lg"
                >
                  View Pricing
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4 pt-6">
                {aboutData.stats.slice(0, 3).map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl md:text-3xl font-bold text-[#f59e0b]">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#1e3a8a]/20 to-[#f59e0b]/20 rounded-2xl transform rotate-3"></div>
              <img
                src={heroData.image}
                alt="Professional Legal Services"
                className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              {aboutData.title}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
          </div>
          <Card className="border-none shadow-lg">
            <CardContent className="p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {aboutData.description}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {aboutData.stats.map((stat, index) => (
                  <div
                    key={index}
                    className="text-center p-6 bg-gradient-to-br from-[#1e3a8a]/5 to-[#f59e0b]/5 rounded-lg"
                  >
                    <div className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-2">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Trademark Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1e3a8a]/5 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              {trademarkOverview.title}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
          </div>
          <Card className="border-none shadow-xl mb-8">
            <CardContent className="p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {trademarkOverview.content}
              </p>
            </CardContent>
          </Card>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trademarkOverview.points.map((point, index) => (
              <Card
                key={index}
                className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow"
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-gradient-to-br from-[#f59e0b] to-[#d97706] rounded-full flex items-center justify-center mb-3">
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <CardTitle className="text-lg font-bold text-[#1e3a8a]">
                    {point.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{point.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Our Trademark Services
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              End-to-end trademark registration and protection services with expert guidance at every step
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {servicesData.map((service, index) => {
              const Icon = iconMap[service.icon];
              return (
                <Card
                  key={index}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  <CardHeader>
                    <div className="w-14 h-14 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-lg flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <CardTitle className="text-xl font-bold text-[#1e3a8a] mb-3">
                      {service.title}
                    </CardTitle>
                    <CardDescription className="text-gray-700 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-600">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Why Choose Us
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Experience the difference that expertise, dedication, and client-focused service make in trademark registration
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {whyChooseUs.map((reason, index) => (
              <Card
                key={index}
                className="border-none shadow-lg bg-white/80 backdrop-blur-sm hover:shadow-xl transition-shadow"
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#f59e0b] to-[#d97706] rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-6 h-6 text-white" />
                    </div>
                    {reason.stats && (
                      <Badge className="bg-[#1e3a8a] text-white hover:bg-[#1e40af]">
                        {reason.stats}
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-xl font-bold text-[#1e3a8a]">
                    {reason.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed">{reason.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Our Trademark Registration Process
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A simple, transparent three-step process to protect your brand
            </p>
          </div>
          <div className="space-y-8">
            {processSteps.map((step, index) => (
              <Card
                key={index}
                className="border-none shadow-lg hover:shadow-xl transition-shadow"
              >
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-full flex items-center justify-center">
                        <span className="text-2xl font-bold text-white">
                          {step.step}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-[#1e3a8a] mb-3">
                        {step.title}
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1e3a8a]/5 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Transparent Pricing Plans
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the package that best fits your trademark protection needs
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPlans.map((plan, index) => (
              <Card
                key={index}
                className={`border-none shadow-lg relative ${
                  plan.recommended
                    ? 'ring-2 ring-[#f59e0b] scale-105'
                    : ''
                }`}
              >
                {plan.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-[#f59e0b] to-[#d97706] text-white px-4 py-1">
                      Recommended
                    </Badge>
                  </div>
                )}
                <CardHeader className="text-center pb-4">
                  <CardTitle className="text-2xl font-bold text-[#1e3a8a]">
                    {plan.name}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-[#1e3a8a]">
                      {plan.price}
                    </span>
                  </div>
                  <CardDescription className="text-sm mt-2">
                    {plan.subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
                    <ul className="space-y-3">
                      {plan.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-gray-500 mt-4 text-center">
                    {plan.note}
                  </p>
                </CardContent>
                <CardFooter>
                  <Button
                    onClick={() => setPopupOpen(true)}
                    className={`w-full ${
                      plan.recommended
                        ? 'bg-gradient-to-r from-[#f59e0b] to-[#d97706] hover:from-[#d97706] hover:to-[#f59e0b]'
                        : 'bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#1e3a8a]'
                    } text-white`}
                  >
                    Get Started
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Mobile Slider */}
          <div className="md:hidden relative">
            <div className="overflow-x-auto snap-x snap-mandatory flex gap-4 pb-4">
              {pricingPlans.map((plan, index) => (
                <div
                  key={index}
                  className="snap-center flex-shrink-0 w-[85%]"
                >
                  <Card
                    className={`border-none shadow-lg h-full ${
                      plan.recommended
                        ? 'ring-2 ring-[#f59e0b]'
                        : ''
                    }`}
                  >
                    {plan.recommended && (
                      <div className="text-center pt-2">
                        <Badge className="bg-gradient-to-r from-[#f59e0b] to-[#d97706] text-white">
                          Recommended
                        </Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-4">
                      <CardTitle className="text-2xl font-bold text-[#1e3a8a]">
                        {plan.name}
                      </CardTitle>
                      <div className="mt-4">
                        <span className="text-4xl font-bold text-[#1e3a8a]">
                          {plan.price}
                        </span>
                      </div>
                      <CardDescription className="text-sm mt-2">
                        {plan.subtitle}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
                        <ul className="space-y-3">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-700">{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <p className="text-xs text-gray-500 mt-4 text-center">
                        {plan.note}
                      </p>
                    </CardContent>
                    <CardFooter>
                      <Button
                        onClick={() => setPopupOpen(true)}
                        className={`w-full ${
                          plan.recommended
                            ? 'bg-gradient-to-r from-[#f59e0b] to-[#d97706]'
                            : 'bg-gradient-to-r from-[#1e3a8a] to-[#1e40af]'
                        } text-white`}
                      >
                        Get Started
                      </Button>
                    </CardFooter>
                  </Card>
                </div>
              ))}
            </div>
            <div className="flex justify-center gap-2 mt-4">
              {pricingPlans.map((_, index) => (
                <div
                  key={index}
                  className="w-2 h-2 rounded-full bg-gray-300"
                ></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries Served */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Industries We Serve
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We protect brands across every industry sector with specialized expertise in your business vertical
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {industriesServed.map((industry, index) => {
              const Icon = iconMap[industry.icon];
              return (
                <Card
                  key={index}
                  className="border-none shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <CardContent className="p-6">
                    <Icon className="w-10 h-10 text-[#1e3a8a] mb-4" />
                    <h3 className="font-bold text-gray-900 text-base mb-2">
                      {industry.name}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {industry.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Benefits of Trademark Registration
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Federal trademark registration provides powerful advantages that protect and grow your business
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <Card
                key={index}
                className="border-none shadow-lg bg-white hover:shadow-xl transition-shadow"
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <Star className="w-8 h-8 text-[#f59e0b] flex-shrink-0" />
                    {benefit.impact && (
                      <Badge variant="outline" className="text-xs border-[#1e3a8a] text-[#1e3a8a]">
                        <Zap className="w-3 h-3 mr-1" />
                        Impact
                      </Badge>
                    )}
                  </div>
                  <CardTitle className="text-xl font-bold text-[#1e3a8a] mb-3">
                    {benefit.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed mb-4">{benefit.description}</p>
                  {benefit.impact && (
                    <div className="bg-gradient-to-r from-[#1e3a8a]/5 to-blue-50 p-4 rounded-lg border-l-4 border-[#f59e0b]">
                      <p className="text-sm font-semibold text-[#1e3a8a]">
                        {benefit.impact}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {trustSignals.map((signal, index) => (
              <Card
                key={index}
                className="border-none shadow-lg text-center bg-gradient-to-br from-[#1e3a8a]/5 to-blue-50"
              >
                <CardHeader>
                  <Award className="w-12 h-12 text-[#f59e0b] mx-auto mb-2" />
                  <CardTitle className="text-lg font-bold text-[#1e3a8a]">
                    {signal.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-700">{signal.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Frequently Asked Questions
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600">
              Get answers to common trademark registration questions
            </p>
          </div>
          <Card className="border-none shadow-xl">
            <CardContent className="p-6">
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left font-semibold text-[#1e3a8a] hover:text-[#f59e0b]">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src={ctaData.image}
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
              {ctaData.title}
            </h2>
            <p className="text-xl text-white/95 mb-6 leading-relaxed">
              {ctaData.subtitle}
            </p>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-6 mb-8">
              <p className="text-lg text-white font-semibold flex items-center justify-center gap-2">
                <Award className="w-6 h-6 text-[#f59e0b]" />
                {ctaData.urgency}
              </p>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {ctaData.stats.map((stat, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 border">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl font-bold text-[#f59e0b] mb-2">
                    {stat.value}
                  </div>
                  <div className="text-white font-semibold mb-1">{stat.label}</div>
                  <div className="text-sm text-white/80">{stat.description}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => setPopupOpen(true)}
              size="lg"
              className="bg-gradient-to-r from-[#f59e0b] to-[#d97706] hover:from-[#d97706] hover:to-[#f59e0b] text-white font-semibold px-10 py-6 text-lg group"
            >
              Protect My Brand Now
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              size="lg"
              variant="outline"
              className="bg-white text-[#1e3a8a] hover:bg-white/90 px-10 py-6 text-lg border-2 border-white"
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] mb-4">
              Get in Touch
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#f59e0b] to-[#d97706] mx-auto mb-6"></div>
            <p className="text-lg text-gray-600">
              Have questions? We're here to help protect your brand
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-bold text-[#1e3a8a] mb-2">Phone</h3>
                <a
                  href={`tel:${companyInfo.phone}`}
                  className="text-gray-700 hover:text-[#f59e0b]"
                >
                  {companyInfo.phone}
                </a>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-bold text-[#1e3a8a] mb-2">Email</h3>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="text-gray-700 hover:text-[#f59e0b] break-all"
                >
                  {companyInfo.email}
                </a>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h3 className="font-bold text-[#1e3a8a] mb-2">Address</h3>
                <p className="text-gray-700">{companyInfo.address}</p>
              </CardContent>
            </Card>
          </div>
          <div className="text-center mt-12">
            <Button
              onClick={() => setPopupOpen(true)}
              size="lg"
              className="bg-gradient-to-r from-[#1e3a8a] to-[#1e40af] hover:from-[#1e40af] hover:to-[#1e3a8a] text-white font-semibold px-12 py-6 text-lg"
            >
              Schedule a Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">{companyInfo.name}</h3>
              <p className="text-white/80 text-sm">
                Professional trademark registration services for businesses across the United States.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                {navItems.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => scrollToSection(item.id)}
                      className="text-white/80 hover:text-white transition-colors"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="/privacy" className="text-white/80 hover:text-white transition-colors">
                    Privacy Policy
                  </a>
                </li>
                <li>
                  <a href="/terms" className="text-white/80 hover:text-white transition-colors">
                    Terms & Conditions
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contact Info</h4>
              <ul className="space-y-2 text-sm text-white/80">
                <li>{companyInfo.phone}</li>
                <li>{companyInfo.email}</li>
                <li>{companyInfo.address}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 text-center text-sm text-white/80">
            <p>© 2026 {companyInfo.name}. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <ContactPopup open={popupOpen} onOpenChange={setPopupOpen} />
    </div>
  );
}
